import type { MilkType, VolumeDisplayUnit } from '@baby-tracker/validation';
import { millilitresToVolume, volumeToMillilitres } from '@baby-tracker/validation';
import { useState, type ReactElement } from 'react';

import { ChoiceChips } from '../../ui/ChoiceChips.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Button } from '../../ui/Button.js';
import { Sheet } from '../../ui/Sheet.js';
import fieldStyles from '../../ui/fields.module.css';
import { TextField } from '../../ui/TextField.js';
import { FormError } from '../../ui/FormError.js';

const milkOptions = [
    { value: 'formula', label: 'Formula' },
    { value: 'breast_milk', label: 'Breast milk' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'other', label: 'Other' },
] as const satisfies readonly { value: MilkType; label: string }[];

const PRESET_ML = [60, 90, 120, 150];

export function BottleSheet({
    open,
    unit,
    recentMl,
    pending,
    error,
    onClose,
    onSave,
}: {
    open: boolean;
    unit: VolumeDisplayUnit;
    recentMl: number[];
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onSave: (input: { milkType: MilkType; volumeMl: number }) => Promise<void>;
}): ReactElement {
    const [milkType, setMilkType] = useState<MilkType>('formula');
    const [amount, setAmount] = useState('');

    const presets = uniqueAmounts(recentMl, unit);

    function step(delta: number): void {
        const current = Number(amount);
        const next = (Number.isFinite(current) ? current : 0) + delta;
        if (next > 0) {
            setAmount(String(next));
        }
    }

    return (
        <Sheet title="Bottle" open={open} onClose={onClose}>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    event.preventDefault();
                    const parsed = Number(amount);
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                        return;
                    }
                    void onSave({ milkType, volumeMl: volumeToMillilitres(parsed, unit) });
                }}
            >
                <SegmentedControl
                    label="Milk"
                    value={milkType}
                    options={milkOptions}
                    onChange={setMilkType}
                />
                <ChoiceChips
                    label="Usual amounts"
                    value={amount}
                    options={presets.map((value) => ({ value, label: value }))}
                    onChange={setAmount}
                />
                <div className={fieldStyles.row}>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            step(-10);
                        }}
                    >
                        −10
                    </Button>
                    <TextField
                        label={`Amount (${unit})`}
                        inputMode="decimal"
                        autoComplete="off"
                        enterKeyHint="done"
                        value={amount}
                        onChange={(event) => {
                            setAmount(event.target.value);
                        }}
                    />
                    <Button
                        variant="secondary"
                        onClick={() => {
                            step(10);
                        }}
                    >
                        +10
                    </Button>
                </div>
                <FormError message={error} />
                <Button type="submit" loading={pending}>
                    Save bottle
                </Button>
            </form>
        </Sheet>
    );
}

function uniqueAmounts(recentMl: number[], unit: VolumeDisplayUnit): string[] {
    const values = [...recentMl, ...PRESET_ML];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const ml of values) {
        const display = String(millilitresToVolume(ml, unit));
        if (seen.has(display)) {
            continue;
        }
        seen.add(display);
        result.push(display);
    }
    return result;
}
